import React, { useState, useEffect } from 'react';
import { api } from '../utils/apiClient';
import { Button, Space, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { TGDataTable } from './common';
import EditMetadataDialog from './EditMetadataDialog';
import DeleteMetadataDialog from './DeleteMetadataDialog';
import AddMetadataDialog from './AddMetadataDialog';

function DocumentSettings() {
  const [metadataFields, setMetadataFields] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      api.get('/metadata')
        .then(response => {
          setMetadataFields(response.data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching metadata fields:', error);
          message.error('Failed to fetch metadata fields');
          setLoading(false);
        });
    };

    fetchData();
  }, []);

  const handleEdit = (updatedField) => {
    api.put(`/metadata/${updatedField.id}`, updatedField)
      .then(() => {
        setMetadataFields(metadataFields.map(field => (field.id === updatedField.id ? updatedField : field)));
        message.success('Metadata field updated successfully');
      })
      .catch(error => {
        console.error('Error updating metadata field:', error);
        message.error('Failed to update metadata field');
      });
  };

  const handleDelete = (metadataId) => {
    api.delete(`/metadata/${metadataId}`)
      .then(() => {
        setMetadataFields(metadataFields.filter(field => field.id !== metadataId));
        message.success('Metadata field deleted successfully');
      })
      .catch(error => {
        console.error('Error deleting metadata field:', error);
        message.error('Failed to delete metadata field');
      });
  };

  const handleAddNew = (newField) => {
    api.post('/metadata', newField)
      .then(response => {
        setMetadataFields([...metadataFields, response.data]);
        message.success('Metadata field added successfully');
      })
      .catch(error => {
        console.error('Error adding new metadata field:', error);
        message.error('Failed to add metadata field');
      });
  };

  const handleEditClick = (metadata) => {
    setSelectedMetadata(metadata);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (metadata) => {
    setSelectedMetadata(metadata);
    setIsDeleteOpen(true);
  };

  const handleAddClick = () => {
    setIsAddOpen(true);
  };

  // Column definitions for TGDataTable
  const columns = [
    {
      key: 'document_type',
      title: 'Document Type',
      dataIndex: 'document_type',
      width: 180,
      fixed: 'left',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'field_label',
      title: 'Field Label',
      dataIndex: 'field_label',
      width: 180,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'field_name',
      title: 'Field Name',
      dataIndex: 'field_name',
      width: 180,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'data_type',
      title: 'Data Type',
      dataIndex: 'data_type',
      width: 120,
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'required',
      title: 'Required',
      dataIndex: 'required',
      width: 100,
      align: 'center',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      key: 'order',
      title: 'Order',
      dataIndex: 'order',
      width: 100,
      align: 'center',
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'is_name_field',
      title: 'Is Name Field',
      dataIndex: 'is_name_field',
      width: 130,
      align: 'center',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Yes', value: true },
        { label: 'No', value: false },
      ],
      render: (value) => (value ? 'Yes' : 'No'),
    },
    {
      key: 'options',
      title: 'Options',
      dataIndex: 'options',
      ellipsis: true,
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            title="Edit Metadata"
          />
          <Button
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            title="Delete Metadata"
            danger
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddClick}
          size="large"
        >
          Add Metadata Field
        </Button>
      </div>

      <TGDataTable
        data={metadataFields}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination="client"
        pageSize={10}
        showExport
        showColumnConfig
        exportFilename="document-settings"
        presetKey="admin.document-settings"
      />

      {selectedMetadata && (
        <EditMetadataDialog
          open={isEditOpen}
          metadata={selectedMetadata}
          onClose={() => setIsEditOpen(false)}
          onSave={(updatedField) => {
            handleEdit(updatedField);
            setIsEditOpen(false);
          }}
        />
      )}

      {selectedMetadata && (
        <DeleteMetadataDialog
          open={isDeleteOpen}
          metadata={selectedMetadata}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => {
            handleDelete(selectedMetadata.id);
            setIsDeleteOpen(false);
          }}
        />
      )}

      <AddMetadataDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSave={(newField) => {
          handleAddNew(newField);
          setIsAddOpen(false);
        }}
      />
    </div>
  );
}

export default DocumentSettings;
